import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import { KeyHeader } from '../../shared/TableComponent';

import { BarChart2, Plus } from 'lucide-react';
import { useTranslate } from 'sbx-crm-menu-ui';

import { useNavigate } from 'react-router-dom';
import TableComponentV2 from 'src/components/shared/TableComponentV2';
import { getAllJobsService } from 'src/services/DocumentOrImageV2Service';
import { appActions } from 'src/store';
import { DocumentResults } from 'src/types/Document';
import { ModalType } from 'src/types/Modal';
import { AppAgentContext } from '../../../context/ContextProvider';
import { DateUtil, validatePermissionModule } from '../../../utils';
import NavbarBreadcrumbV2 from '../../shared/NavbarBreadcrumbV2';
import { Button } from '../../ui/button';
import { DOCUMENT_V2_CREATE, Permissions, PermissionType } from '../../../types/Permission';
import { DocumentOrImageV2 } from '../../../types/DocumentOrImageV2';
import { DeniedComponent } from 'src/App';
import Permission from 'src/components/shared/Permission';

const DOCUMENT_V2 = Permissions.SBX_STUDIO_DOCUMENT_V2;

const DocumentListComponentV2 = () => {
    const {
        state: {
            session: { user_id, permissions_list },
        },
    } = useContext(AppAgentContext);
    const { t: common } = useTranslate('common');
    const { t: document } = useTranslate('document');
    const { data, isLoading, isRefetching } = useQuery({
        queryKey: ['documents'],
        queryFn: async () =>
            await getAllJobsService({ user: user_id, key_filter: 'document_analysis', job_type: 'Chat' }),
    });
    const { dispatch } = useContext(AppAgentContext);
    // const [dateFrom, setDateFrom] = useState<Date>();

    const validateByPermissionType = ({
        document,
        permissionType,
    }: {
        document: DocumentOrImageV2;
        permissionType: PermissionType;
    }) => {
        const keys = validatePermissionModule({
            permissions_list,
            module: Permissions.SBX_STUDIO_DOCUMENT_V2,
            types: [permissionType],
        });

        if (!document.id) {
            return false;
        }
        return keys.length === 0 || keys.includes(document.id);
    };

    const dataSort = useMemo(() => {
        if (data?.success) {
            return data.items
                .filter((document) => {
                    return validateByPermissionType({ document, permissionType: PermissionType.READ });
                })
                .filter((item) => item.getResults().sentiment_analysis_resume)
                .sort((a, b) => {
                    const utcDateA = !a.created_at.includes('Z') ? a.created_at + 'Z' : a.created_at;
                    const utcDateB = !b.created_at.includes('Z') ? b.created_at + 'Z' : b.created_at;
                    return new Date(utcDateB).getTime() - new Date(utcDateA).getTime();
                });
        }
        return [];
    }, [data]);

    const navigate = useNavigate();

    return (
        <Permission permission={DOCUMENT_V2} denied={<DeniedComponent />}>
            <div className="min-h-screen bg-gradient-to-br from-fuchsia-100 via-sky-100 to-violet-200 p-4 sm:p-8">
                <div className="max-w-6xl mx-auto">
                    <NavbarBreadcrumbV2
                        routes={[
                            {
                                label: document('document'),
                                route: '',
                            },
                        ]}
                    />

                    <h1 className="title">{document('document')}</h1>

                    <TableComponentV2
                        items={dataSort}
                        loading={isLoading || isRefetching}
                        topActions={
                            <Permission permission={DOCUMENT_V2_CREATE}>
                                <div>
                                    <Button
                                        className="primary-gradient-button"
                                        onClick={() => {
                                            navigate('/documents-v2/analyze-document');
                                        }}
                                    >
                                        <Plus size={20} className="mr-2" /> {document('analyze_document')}
                                    </Button>
                                </div>
                            </Permission>
                        }
                        pagination
                        search
                        header={[
                            {
                                type: KeyHeader.KEY,
                                label: common('type'),
                                key: 'type',
                            },
                            {
                                type: KeyHeader.KEY,
                                label: common('status'),
                                key: 'status',
                            },
                            {
                                type: KeyHeader.VALUE,
                                label: common('createdDate'),
                                toValue: (item) => {
                                    if (item.created_at) {
                                        const utcDate = !item.created_at.includes('Z')
                                            ? item.created_at + 'Z'
                                            : item.created_at;
                                        return new DateUtil(new Date(utcDate), 'yyyy-MM-dd hh:mm:ss a').getDate();
                                    }
                                    return new DateUtil(new Date(), 'yyyy-MM-dd hh:mm:ss a').getDate();
                                },
                            },
                        ]}
                        actions={[
                            {
                                label: 'Ver Resultados',
                                icon: <BarChart2 className="h-4 w-4" />,
                                className:
                                    'h-8 w-8 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-600 flex items-center justify-center',
                                onClick: (item) => {
                                    dispatch(
                                        appActions.openModal({
                                            type: ModalType.DOCUMENT_RESULTS,
                                            data: {
                                                title: '',
                                                onClose: () =>
                                                    dispatch(appActions.closeModal(ModalType.DOCUMENT_RESULTS)),
                                                data: {
                                                    ...item.getResults(),
                                                    source: item.getSource() ?? '',
                                                    agent: item.getAgent() ?? '',
                                                } as DocumentResults,
                                            },
                                        })
                                    );
                                },
                            },
                        ]}
                    />
                </div>
            </div>
        </Permission>
    );
};

export default DocumentListComponentV2;
